import { useContext, useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { ParticipantContext } from "../../contexts/Participant.context";
import { PageProps } from "../../models/routing.model";
import { classNames } from "primereact/utils";
import TwilioConversationService from "../../services/twilio/twilio-conversations.service";
import ParticipantProfileInfo from "./participant-profile/ParticipantProfile";
import { useParticipantProfile } from "../../hooks/useParticipantProfile";
import ParticipantState from "./ParticipantState";
import { useMatomoTrackParticipant } from "../../hooks/useMatomoTrackParticipant";
import { EhrProvider } from "../../models/entities/ehr-provider.model";

const Participant = (props: PageProps) => {
	// URL param: participant ID
	const urlParams = useParams<{ participantId: string }>();
	const parentContext = useContext(ParticipantContext);

	// EHR providers: fetched in ParticipantProfile, passed here via callback, then provided to EhrMrn via context
	const [allEhrProviders, setAllEhrProviders] = useState<EhrProvider[] | undefined>(undefined);

	// GET: Participant by ID
	const participantProfileQuery = useParticipantProfile({
		participantId: Number(urlParams.participantId),
	});

	/* MATOMO (usage tracking) */

	useMatomoTrackParticipant();

	/* Twilio conversations */

	const initializeTwilioConversations = async (participantId: number): Promise<void> => {
		await TwilioConversationService.initializeClient();
		await TwilioConversationService.getConversation(participantId);
	};

	useEffect(() => {
		const participantId = urlParams.participantId ? Number(urlParams.participantId) : null;
		if (participantId) {
			initializeTwilioConversations(participantId);
		}
	}, [urlParams.participantId]);

	// LOADING / ERROR
	if (participantProfileQuery.isLoading || participantProfileQuery.isError) {
		return (
			<div className="participant-container">
				<section className="participant-profile-container">
					<ParticipantState
						isLoading={participantProfileQuery.isLoading}
						isError={participantProfileQuery.isError}
					/>
				</section>
			</div>
		);
	}

	return (
		<ParticipantContext.Provider
			value={{
				...parentContext,
				allEhrProviders,
			}}
		>
			<div className="participant-container">
				{/* Participant profile */}
				<section
					className={classNames(
						"participant-profile-container",
						participantProfileQuery.data?.status
					)}
				>
					<ParticipantProfileInfo
						onRefresh={() => participantProfileQuery.invalidate()}
						onEhrProvidersLoad={setAllEhrProviders}
					/>
				</section>

				{/* Pages content */}
				<div className="participant-content">
					<Outlet />
				</div>
			</div>
		</ParticipantContext.Provider>
	);
};
export default Participant;
